import * as React from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  createLegalEntityFromGus,
  createLegalEntityUnverified,
  enrollLegalEntity,
  lookupLegalEntity,
  LegalEntityApiError,
  type GusPreview,
  type LegalEntityKind,
  type LegalEntityPublic,
} from "@/lib/legalEntityApi";
import {
  LEGAL_ENTITY_KIND_LABELS,
  legalEntityErrorMessage,
} from "@/lib/legalEntityMessages";
import { VERIFICATION_ALERTS_ROOT } from "@/lib/legalEntityApi";
import { LegalEntityUnverifiedForm, type UnverifiedFormValues } from "@/components/legal-entity/LegalEntityUnverifiedForm";
import { VerificationNeededBadge } from "@/components/legal-entity/VerificationNeededBadge";

export type LegalEntityModuleFlags = {
  isCleaning?: boolean;
  isMaintenance?: boolean;
  isAdmin?: boolean;
};

export interface LegalEntityNipFieldProps {
  orgId: string;
  value: LegalEntityPublic | null;
  onChange: (entity: LegalEntityPublic | null) => void;
  allowedKinds: LegalEntityKind[];
  flags: LegalEntityModuleFlags;
  required?: boolean;
  disabled?: boolean;
  optionalHint?: string;
}

function digitsOnly(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 10);
}

function optionalEmailOk(raw: string): boolean {
  const value = raw.trim();
  if (value.length === 0) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function optionalPhoneOk(raw: string): boolean {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 0) return true;
  return digits.length >= 9;
}

function kindAllowed(kind: string, allowed: LegalEntityKind[]): kind is LegalEntityKind {
  return allowed.includes(kind as LegalEntityKind);
}

function isGusOutageCode(code: string): boolean {
  return (
    code === "gus_unavailable" ||
    code === "GUS_NOT_CONFIGURED" ||
    code === "GUS_LOGIN_FAILED" ||
    code === "GUS_FAILED" ||
    code.startsWith("GUS_HTTP_")
  );
}

export function LegalEntityNipField({
  orgId,
  value,
  onChange,
  allowedKinds,
  flags,
  required = false,
  disabled = false,
  optionalHint,
}: LegalEntityNipFieldProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [nip, setNip] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [gus, setGus] = React.useState<GusPreview | null>(null);
  const [unverifiedOpen, setUnverifiedOpen] = React.useState(false);
  const [kind, setKind] = React.useState<LegalEntityKind>(allowedKinds[0] ?? "company");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [shortName, setShortName] = React.useState("");

  React.useEffect(() => {
    if (value) {
      setNip(value.nip);
      setGus(null);
      setUnverifiedOpen(false);
      setMessage(null);
    }
  }, [value]);

  const clearSelection = () => {
    onChange(null);
    setGus(null);
    setUnverifiedOpen(false);
    setMessage(null);
  };

  const openUnverified = () => {
    setGus(null);
    setUnverifiedOpen(true);
    setMessage(null);
  };

  const handleLookup = async () => {
    setBusy(true);
    setMessage(null);
    setGus(null);
    setUnverifiedOpen(false);
    try {
      const result = await lookupLegalEntity(orgId, nip);
      if (result.status === "invalid_nip") {
        setMessage(legalEntityErrorMessage("invalid_nip"));
        return;
      }
      if (result.status === "exists_in_domio" && result.entity) {
        if (!kindAllowed(result.entity.kind, allowedKinds)) {
          setMessage(
            t("admin.legalEntity.kindMismatch", {
              kind: LEGAL_ENTITY_KIND_LABELS[result.entity.kind],
            }),
          );
          return;
        }
        if (!result.alreadyEnrolledInThisOrg) {
          const enrolled = await enrollLegalEntity({
            orgId,
            legalEntityId: result.entity.id,
            ...flags,
          });
          onChange(enrolled.entity);
        } else {
          onChange(result.entity);
        }
        return;
      }
      if (result.status === "found_in_gus" && result.gusPreview) {
        setGus(result.gusPreview);
        const suggested = result.suggestedKind;
        if (suggested && kindAllowed(suggested, allowedKinds)) {
          setKind(suggested);
        }
        setShortName(result.gusPreview.legalName.slice(0, 80));
        return;
      }
      if (result.status === "gus_unavailable" || isGusOutageCode(result.status)) {
        openUnverified();
        return;
      }
      setMessage(legalEntityErrorMessage(result.status));
    } catch (err) {
      console.error("[LegalEntityNipField] lookup:", err);
      const code = err instanceof LegalEntityApiError ? err.code : "RPC_FAILED";
      if (isGusOutageCode(code)) {
        openUnverified();
        return;
      }
      setMessage(err instanceof LegalEntityApiError ? err.message : legalEntityErrorMessage("RPC_FAILED"));
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async () => {
    if (!gus) return;
    setBusy(true);
    setMessage(null);
    try {
      const created = await createLegalEntityFromGus({
        orgId,
        nip: gus.nip,
        kind,
        email,
        phone,
        shortName,
        ...flags,
      });
      onChange(created.entity);
      setGus(null);
    } catch (err) {
      console.error("[LegalEntityNipField] create:", err);
      const code = err instanceof LegalEntityApiError ? err.code : "RPC_FAILED";
      if (isGusOutageCode(code)) {
        openUnverified();
        return;
      }
      setMessage(err instanceof LegalEntityApiError ? err.message : legalEntityErrorMessage("RPC_FAILED"));
    } finally {
      setBusy(false);
    }
  };

  const handleUnverifiedCreate = async (values: UnverifiedFormValues) => {
    setBusy(true);
    setMessage(null);
    try {
      const created = await createLegalEntityUnverified({
        orgId,
        nip,
        kind: values.kind,
        shortName: values.shortName,
        legalName: values.legalName,
        email: values.email,
        phone: values.phone,
        city: values.city,
        postalCode: values.postalCode,
        street: values.street || null,
        buildingNumber: values.buildingNumber || null,
        ...flags,
      });
      onChange(created.entity);
      setUnverifiedOpen(false);
      await queryClient.invalidateQueries({ queryKey: [VERIFICATION_ALERTS_ROOT] });
    } catch (err) {
      console.error("[LegalEntityNipField] unverified:", err);
      setMessage(err instanceof LegalEntityApiError ? err.message : legalEntityErrorMessage("RPC_FAILED"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-3">
      <div className="grid gap-2">
        <Label htmlFor="legal-entity-nip">{required ? "NIP" : t("admin.legalEntity.nipOptional")}</Label>
        <div className="flex gap-2">
          <Input
            id="legal-entity-nip"
            inputMode="numeric"
            autoComplete="off"
            placeholder={t("admin.legalEntity.nipPlaceholder")}
            value={nip}
            disabled={disabled || busy || Boolean(value)}
            onChange={(e) => setNip(digitsOnly(e.target.value))}
          />
          <Button
            type="button"
            variant="secondary"
            disabled={disabled || busy || nip.length !== 10 || Boolean(value)}
            onClick={() => void handleLookup()}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("admin.legalEntity.checkNip")}
          </Button>
        </div>
        {!required && optionalHint ? (
          <p className="text-xs text-muted-foreground">{optionalHint}</p>
        ) : null}
      </div>

      {value ? (
        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium">{value.shortName}</p>
            {value.verificationStatus === "pending_manual" ? <VerificationNeededBadge /> : null}
          </div>
          <p className="text-muted-foreground">{value.legalName}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {LEGAL_ENTITY_KIND_LABELS[value.kind]} · NIP {value.nip}
          </p>
          <p className="text-xs text-muted-foreground">{value.seatFullAddress}</p>
          <Button type="button" variant="ghost" size="sm" className="mt-2 h-8 px-2" onClick={clearSelection} disabled={disabled}>
            {t("admin.legalEntity.clear")}
          </Button>
        </div>
      ) : null}

      {gus && !value ? (
        <div className="grid gap-3 rounded-md border p-3">
          <div className="text-sm">
            <p className="font-medium">{gus.legalName}</p>
            <p className="text-xs text-muted-foreground mt-1">{gus.seatFullAddress}</p>
            <p className="text-xs text-muted-foreground">REGON {gus.regon ?? "—"}</p>
          </div>
          <div className="grid gap-2">
            <Label>{t("admin.legalEntity.entityKind")}</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as LegalEntityKind)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {allowedKinds.map((k) => (
                  <SelectItem key={k} value={k}>
                    {LEGAL_ENTITY_KIND_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="legal-entity-short">{t("admin.legalEntity.shortName")}</Label>
            <Input
              id="legal-entity-short"
              value={shortName}
              onChange={(e) => setShortName(e.target.value.slice(0, 80))}
              disabled={busy}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="legal-entity-email">{t("admin.legalEntity.contactEmail")}</Label>
            <Input
              id="legal-entity-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="legal-entity-phone">{t("admin.legalEntity.phone")}</Label>
            <Input
              id="legal-entity-phone"
              type="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={busy}
            />
          </div>
          <Button type="button" onClick={() => void handleCreate()} disabled={busy || !optionalEmailOk(email) || !optionalPhoneOk(phone)}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t("admin.legalEntity.addToDomio")}
          </Button>
        </div>
      ) : null}

      {unverifiedOpen && !value ? (
        <LegalEntityUnverifiedForm
          allowedKinds={allowedKinds}
          busy={busy}
          initialKind={kind}
          onSubmit={(values) => void handleUnverifiedCreate(values)}
          onCancel={() => setUnverifiedOpen(false)}
        />
      ) : null}

      {message ? <p className="text-sm text-destructive">{message}</p> : null}
    </div>
  );
}
